import {BaseComponent} from '../base/component.base.js';
import {escapeHtml} from '../../core/escape-html.js';
import template from "./client-list.template.html";
import styles from "./client-list.styles.css";

export class ClientListComponent extends BaseComponent {

    connectedCallback() {
        this.loadTemplate(template, styles);

        this.$('#create-form').addEventListener('submit', (e) => {
            e.preventDefault();
            const form = e.target;
            this.emit('client-create', {
                slug: form.slug.value,
                name: form.name.value,
                repoName: form.repoName.value,
                domain: form.domain.value,
                basePath: form.basePath.value,
                userCreationGuid: form.userCreationGuid.value,
            });
        });
    }

    /** Clears the create form; the driving adapter calls this once the client is created. */
    resetCreateForm() {
        this.$('#create-form')?.reset();
    }

    /**
     * Shows the loading overlay and disables the create button, so a request cannot be sent twice.
     *
     * @param {boolean} value
     */
    set loading(value) {
        const overlay = this.$('#loading-overlay');
        if (overlay) {
            overlay.hidden = !value;
        }
        const submit = this.$('#create-form button[type="submit"]');
        if (submit) {
            submit.disabled = value;
        }
    }

    /** @param {import('../../shell/types/client.types.js').ClientViewModel[]} clients */
    set clients(clients) {
        const container = this.$('#client-list');
        if (!container) {
            return;
        }

        container.innerHTML = '';

        if (clients.length === 0) {
            const empty = document.createElement('empty-state');
            const title = document.createElement('span');
            title.slot = 'title';
            title.textContent = 'No clients yet';
            empty.appendChild(title);
            empty.appendChild(document.createTextNode('Create the first one using the form below.'));
            container.appendChild(empty);
            return;
        }

        for (const client of clients) {
            const row = document.createElement('div');
            row.className = 'client-row';
            const name = escapeHtml(client.name);
            const templateCount = `${client.templateCount} ${client.templateCount === 1 ? 'Template' : 'Templates'}`;

            row.innerHTML = `
                <div class="client-main">
                    <div class="client-icon-wrapper">
                        ${escapeHtml(client.name.charAt(0).toUpperCase())}
                    </div>
                    <div class="client-details">
                        <h3 class="client-name">${name} <span class="badge badge-gray">${escapeHtml(client.slug)}</span></h3>
                        <div class="client-meta">
                            <div class="meta-item" title="Repository">
                                <i aria-hidden="true" class="fa-brands fa-github"></i>
                                <span>${escapeHtml(client.repoName)}</span>
                            </div>
                            <div class="meta-item" title="Domain">
                                <i aria-hidden="true" class="fa-solid fa-globe"></i>
                                <span>${escapeHtml(client.domain || 'Not set')}</span>
                            </div>
                            <div class="meta-item" title="Registration Code">
                                <i aria-hidden="true" class="fa-solid fa-key"></i>
                                <code>${escapeHtml(client.userCreationGuid)}</code>
                                <button type="button" class="btn btn-ghost btn-sm btn-copy" aria-label="Copy registration code for ${name}"
                                    ${client.userCreationGuid ? '' : 'hidden'}>
                                    <i aria-hidden="true" class="fa-regular fa-copy"></i>
                                </button>
                            </div>
                            <div class="meta-item">
                                <span class="badge badge-blue">${templateCount}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="client-actions">
                    <button type="button" class="btn btn-outline btn-sm btn-view" data-slug="${escapeHtml(client.slug)}" aria-label="View client ${name}">
                        <i aria-hidden="true" class="fa-solid fa-eye"></i> View
                    </button>
                    <button type="button" class="btn btn-delete btn-sm btn-remove" data-slug="${escapeHtml(client.slug)}" aria-label="Delete client ${name}">
                        <i aria-hidden="true" class="fa-solid fa-trash-can"></i> Delete
                    </button>
                </div>
            `;

            row.querySelector('.btn-copy').addEventListener('click', () => {
                this.emit('clipboard-copy', {text: client.userCreationGuid, label: 'Registration code'});
            });

            row.querySelector('.btn-view').addEventListener('click', () => {
                this.emit('client-select', {slug: client.slug});
            });

            row.querySelector('.btn-remove').addEventListener('click', async () => {
                const dialog = document.querySelector('admin-confirm-dialog');
                const confirmed = await dialog.ask({
                    title: `Delete client "${client.name}"?`,
                    body: 'This removes the client and access for its users. Templates and pages on the published site are not deleted.',
                    confirmLabel: 'Delete client',
                    cancelLabel: 'Cancel',
                    danger: true,
                });
                if (confirmed) {
                    this.emit('client-delete', {slug: client.slug});
                }
            });

            container.appendChild(row);
        }
    }
}

customElements.define('client-list', ClientListComponent);
